import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { usePifDiff, useDiffMark, useDiffTitle } from './PifDiffContext';
import { richWordDiff } from 'helpers/richTextDiff';
import DiffWords from './DiffWords';

// A rich-text (HTML) PIF field in the diff view.
//
// When the field CHANGED and highlighting is on, render a WORD-LEVEL diff of the
// new value — only the added/changed words are highlighted (a word struck
// through in this version counts), the rest reads normally with its own strike —
// instead of filling the whole field box (which wrongly implies the entire
// content changed). This mirrors the Changes-list view.
//
// For added / removed fields (or when not highlighting) it renders the raw HTML
// with the existing whole-field mark, since there's no "partial" story to tell.
//
// The word-level view is built from TEXT NODES only, so it cannot survive
// structured content: paragraphs, lists, line breaks, bold, colour and images
// all collapse into one run of words separated by single spaces. Salespeople
// type whole numbered clauses into these boxes (BDS3's "Top Coat Paint" carries
// items 7 and 8), and the review screen was showing them as one unreadable
// paragraph — looking nothing like what was typed, while the print view and the
// PDF were right. Such a value is rendered as real HTML with the whole-field
// mark instead; the word-level diff stays for the flat values where it costs
// nothing and says more.
const BLOCK_MARKUP = /<(p|br|div|ol|ul|li|table|tr|td|img|h[1-6])\b/i;

const DiffRichText = ({ path, html, className = '' }) => {
  const { changeByPath, enabled } = usePifDiff();
  const mark = useDiffMark();
  const title = useDiffTitle();

  const change = enabled ? changeByPath?.[path] : null;
  const parts = useMemo(
    () => (change?.kind === 'changed' ? richWordDiff(change.old, change.new) : null),
    [change]
  );

  const structured = BLOCK_MARKUP.test(html || '') || BLOCK_MARKUP.test(change?.old || '');

  if (!structured && parts?.oldParts.length && parts.newParts.length) {
    return <DiffWords parts={parts.newParts} tone="new" dimUnchanged={false} className={className} />;
  }

  const m = mark(path);
  return (
    <span
      className={`${className}${m}`}
      title={m ? title(path) : undefined}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

DiffRichText.propTypes = {
  path: PropTypes.string,
  html: PropTypes.string,
  className: PropTypes.string,
};

export default DiffRichText;
